// QR Kiosk v2 — KV2-8 guest-pass banner. Rendered inside KioskV2Layout for EVERY
// kiosk surface but self-hides unless the signed-in principal is a Guest. Surfaces the
// guest name, owner company, and a live countdown to passExpiresAt; auto-handles both
// expiry classes:
//   • JWT session lapses (≤12h) but the pass is still valid + we hold the magic-link
//     token → silently re-mint the JWT (before any kiosk API call can 401).
//   • Pass window (passExpiresAt) lapses → drop the session and bounce to the guest
//     landing in its expired/re-redeem state.
// Auth is the single kiosk accessToken; this reads the DISPLAY companion record only.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import accountContext from "../../../account/accountContext";
import { refreshAccount } from "../../../account/accountSlice";
import { Glyph, GD } from "../shared/glyphs";
import { redeemMagicLink, guestRedeemErrorKind } from "./guestAccessApi";
import { readGuestPassMeta, storeGuestSession, clearGuestSession, jwtExpMs, isGuestRole } from "./guestSession";

// Re-mint this many ms before the JWT `exp` so a live request never races a 401.
const REMINT_LEAD_MS = 60_000;
const SOON_MS = 15 * 60_000;

export function GuestPassBanner() {
  const { translate } = useTranslation("public");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const user = useAppSelector((s) => s.account.user);
  const guest = isGuestRole(user?.role);

  const [meta] = useState(() => readGuestPassMeta());
  const [now, setNow] = useState(Date.now());
  const reMinting = useRef(false);

  useEffect(() => {
    if (!guest || !meta) return;
    const passEnd = Date.parse(meta.passExpiresAt);

    const tick = () => {
      const t = Date.now();
      setNow(t);

      if (!Number.isNaN(passEnd) && t >= passEnd) {
        clearGuestSession();
        dispatch(refreshAccount(null));
        // KV2-T1: machine-scoped re-entry (the chooser) instead of the standalone landing —
        // the guest's PIN is in their email; land them back on THIS machine's entry screen.
        navigate(`/public-v2/machines/${encodeURIComponent(meta.machineInternalNumber)}?expired=1`, { replace: true });
        return;
      }

      const jwtEnd = jwtExpMs(accountContext.accessToken);
      if (meta.redeemToken && jwtEnd && t >= jwtEnd - REMINT_LEAD_MS && !reMinting.current) {
        reMinting.current = true;
        redeemMagicLink(meta.redeemToken)
          .then((result) => {
            storeGuestSession(result, meta.redeemToken);
            dispatch(refreshAccount(null));
          })
          .catch((err) => {
            // A uniform 4xx here = pass revoked/expired server-side → treat as expired.
            // Transient (network / 429) → leave the session and retry next window.
            if (guestRedeemErrorKind(err) === "invalid") {
              clearGuestSession();
              dispatch(refreshAccount(null));
              navigate(`/public-v2/machines/${encodeURIComponent(meta.machineInternalNumber)}?expired=1`, { replace: true });
            }
          })
          .finally(() => {
            reMinting.current = false;
          });
      }
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [guest, meta, dispatch, navigate]);

  if (!guest || !meta) return null;

  const passEnd = Date.parse(meta.passExpiresAt);
  const remaining = Number.isNaN(passEnd) ? NaN : passEnd - now;

  let expiryText = "";
  if (!Number.isNaN(remaining)) {
    if (remaining <= 0) {
      expiryText = translate("kioskV2GuestExpiredNow");
    } else {
      const totalMin = Math.floor(remaining / 60_000);
      const hours = Math.floor(totalMin / 60);
      const mins = totalMin % 60;
      if (hours >= 1) expiryText = translate("kioskV2GuestExpiresHm", { h: hours, m: mins });
      else if (totalMin >= 1) expiryText = translate("kioskV2GuestExpiresM", { m: totalMin });
      else expiryText = translate("kioskV2GuestExpiresSoon");
    }
  }
  const soon = !Number.isNaN(remaining) && remaining <= SOON_MS;

  const displayName = user?.firstName || meta.guestName || "";
  const nameLine = `${displayName} ${user?.lastName || ""}`.trim() || translate("kioskV2GuestBannerLabel");

  return (
    <div
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 14px",
        background: "var(--tier-accent-soft)",
        borderBottom: "1px solid var(--tier-accent-line)",
      }}
    >
      <span style={{ color: "var(--tier-accent)", display: "inline-flex", flexShrink: 0 }}>
        <Glyph d={GD.clip} size={18} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {nameLine}
        </div>
        <div style={{ fontSize: 11.5, color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {meta.ownerCompanyName
            ? translate("kioskV2GuestBannerOf", { company: meta.ownerCompanyName })
            : translate("kioskV2GuestBannerLabel")}
        </div>
      </div>
      {expiryText && (
        <span
          style={{
            flexShrink: 0,
            fontFamily: "var(--font-mono)",
            fontSize: 11.5,
            fontWeight: 600,
            color: soon ? "var(--amber)" : "var(--tier-accent-hov)",
            background: soon ? "color-mix(in oklab, var(--amber) 12%, var(--card))" : "var(--card)",
            border: `1px solid ${soon ? "color-mix(in oklab, var(--amber) 36%, var(--border))" : "var(--tier-accent-line)"}`,
            borderRadius: 999,
            padding: "3px 9px",
            whiteSpace: "nowrap",
          }}
        >
          {expiryText}
        </span>
      )}
    </div>
  );
}

export default GuestPassBanner;
